/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import classNames from "clsx";

import InfiniteScroll from "react-infinite-scroll-component";
import { FiFilter } from "react-icons/fi";
import { BsListUl } from "react-icons/bs";
import Col from "react-bootstrap/Col";

import { useApi } from "@/hooks/useApi";
import { useBeds } from "@/hooks/useBeds";
import { usePagination } from "@/hooks/usePagination";
import { BedStatus } from "@/enums/BedStatus";
import { useDebounce } from "@/../_metronic/helpers";
import { transformData } from "@/utils/transformData";
import { useThemeMode } from "@/../_metronic/partials";
import { surgicalTeamsMap } from "@/utils/mappings/surgicalTeams";
import { useWindowDimensions } from "@/hooks/useWindowDimensions";
import { BREAKPOINTS } from "@/styles/constants/breakpoints";

import { SearchFormControl } from "@/components/FormControl/SearchFormControl";
import { PatientAdmissionModal } from "./PatientAdmissionModal";
import { SkeletonCard } from "@/components/SkeletonCard";
import { AdmissionQueueModal } from "./AdmissionQueueModal";
import { AdmissionQueue } from "./AdmissionQueue";
import { FilterModal } from "./FilterModal";
import { BedCard } from "./BedCard";

import "./styles.scss";

type FetchBedsParams = {
	isSearch?: boolean;
	pageToUse?: number;
	isFilter?: boolean;
};

export function SurgicalCenterPage() {
	const [surgicalTeamsOptions, setSurgicalTeamsOptions] = useState<ReactSelectOption[]>([]);
	const [paginatedBeds, setPaginatedBeds] = useState<PaginatedBeds>({} as PaginatedBeds);
	const [patientAdmissionModalShow, setPatientAdmissionModalShow] = useState(false);
	const [queueAdmissionData, setQueueAdmissionData] = useState<QueueAdmissionData>(
		{} as QueueAdmissionData
	);
	const [admissionQueueIsOpen, setAdmissionQueueIsOpen] = useState(true);
	const [isPatientAdmitted, setIsPatientAdmitted] = useState(false);
	const [filters, setFilters] = useState<Filters>({} as Filters);
	const [filterModalShow, setFilterModalShow] = useState(false);
	const [admissionQueueModalShow, setAdmissionQueueModalShow] = useState(false);
	const [search, setSearch] = useState("");

	const { page, forcePage, isFirstRender, handleNextPage, resetPagination } = usePagination();
	const isFirstRenderSearching = useRef(isFirstRender.current);
	const isFirstRenderFiltering = useRef(isFirstRender.current);

	const debouncedSearch = useDebounce(search, 750);
	const { getSurgicalBeds } = useBeds();
	const { mode } = useThemeMode();
	const { api } = useApi();
	const { width } = useWindowDimensions();

	const SKELETON_COUNT = 6;
	const BED_CARD_HEIGHT = "h-18rem";
	const SEARCH_PLACEHOLDER = "Leito - 1.1";
	const IS_HIDDEN_ON_DESKTOP = width >= BREAKPOINTS.DESKTOP_MEDIUM;
	const IS_DESKTOP_MEDIUM = width <= BREAKPOINTS.DESKTOP_MEDIUM;

	const results = paginatedBeds.results;
	const dataLength = results?.length || 1;
	const hasResults = Boolean(results?.length);
	const shouldShowSkeletons = !hasResults;

	const handlePatientAdmissionModalShow = () => setPatientAdmissionModalShow(true);
	const handleFilterModalShow = () => setFilterModalShow(true);
	const handleAdmissionQueueModalShow = () => setAdmissionQueueModalShow(true);

	async function fetchBeds(fetchBedsParams: FetchBedsParams = {}) {
		const { isSearch, pageToUse, isFilter } = fetchBedsParams;
		const currentPage = pageToUse || (isSearch || isFilter ? 1 : page);
		const beds = await getSurgicalBeds(currentPage, search, true, filters);

		if (isSearch || isFilter) {
			setPaginatedBeds(beds);
		} else {
			const combinedResults = [...(results || []), ...(beds.results || [])];
			setPaginatedBeds({ ...beds, results: combinedResults });
		}
	}

	function handleAdmitPatientByTheBed(bed: Bed) {
		if (bed.status === BedStatus.AVAILABLE) {
			handlePatientAdmissionModalShow();
			setQueueAdmissionData({
				...queueAdmissionData,
				attendanceId: null,
				surgicalAttendanceId: null,
				expertiseId: null,
				isQueueAdmission: false,
				bed,
			});
		}
	}

	async function getSurgicalTeams() {
		try {
			const { data } = await api.get("surgical-center/teams");
			const transformedSurgicalTeams = transformData<SurgicalTeam[]>(data, surgicalTeamsMap);
			const options = transformedSurgicalTeams.map((surgicalTeam) => ({
				value: surgicalTeam.id,
				label: surgicalTeam.name,
			}));
			setSurgicalTeamsOptions(options);
		} catch (error) {
			console.log(error);
		}
	}

	function handleNextPageWrapper(pageToUse: number) {
		fetchBeds({ pageToUse });
	}

	function renderSkeletonLoader() {
		const skeletonArray = Array.from({ length: SKELETON_COUNT });
		const colSize = admissionQueueIsOpen ? 6 : 4;
		const colSizeSm = admissionQueueIsOpen ? 12 : 6;

		return skeletonArray.map((_, index) => (
			<SkeletonCard
				key={`skeleton-${index}`}
				index={index}
				colSize={colSize}
				colSizeSm={colSizeSm}
				height={BED_CARD_HEIGHT}
			/>
		));
	}

	function renderBedList() {
		if (!hasResults) {
			return null;
		}
		return results.map((bed, index) => (
			<BedCard
				key={`bed-card-${index}`}
				bed={bed}
				index={index}
				admissionQueueIsOpen={admissionQueueIsOpen}
				onAdmitPatient={handleAdmitPatientByTheBed}
			/>
		));
	}

	function renderContent() {
		const content = renderBedList();

		if (content) {
			return content;
		}

		if (shouldShowSkeletons) {
			return renderSkeletonLoader();
		}

		return null;
	}

	function getContainerClassName() {
		if (admissionQueueIsOpen) {
			return "smaller-patients-container";
		}
		if (IS_DESKTOP_MEDIUM) {
			return "total-patients-container";
		}
		return "larger-patients-container";
	}

	function getInfiniteScrollHeight() {
		return "68.5vh";
	}

	// Função para recarregar a fila de admissão quando um paciente for admitido
	function handlePatientAdmitted() {
		// Força uma atualização dos dados
		setIsPatientAdmitted(!isPatientAdmitted);
	}

	useEffect(() => {
		forcePage.current = page;
		fetchBeds();
	}, [page]);

	useEffect(() => {
		if (isFirstRenderSearching.current) {
			isFirstRenderSearching.current = false;
			return;
		}

		resetPagination();
		setPaginatedBeds({} as PaginatedBeds);
		fetchBeds({ isSearch: true });
	}, [debouncedSearch, isPatientAdmitted]);

	useEffect(() => {
		if (isFirstRenderFiltering.current) {
			isFirstRenderFiltering.current = false;
			return;
		}

		resetPagination();
		setPaginatedBeds({} as PaginatedBeds);
		fetchBeds({ isFilter: true });
	}, [filters]);

	useEffect(() => {
		getSurgicalTeams();
	}, []);

	useEffect(() => {
		if (IS_DESKTOP_MEDIUM) {
			setAdmissionQueueIsOpen(false);
		}
	}, [width]);

	return (
		<div className="d-flex" id="surgical-center-admission-container">
			<div className={getContainerClassName()}>
				<div className="d-flex justify-content-between mb-7">
					<Col lg={admissionQueueIsOpen ? 5 : 3} md={6}>
						<SearchFormControl
							placeholder={SEARCH_PLACEHOLDER}
							onChange={(event) => setSearch(event.target.value)}
						/>
					</Col>

					<Col
						lg={admissionQueueIsOpen ? 7 : 9}
						md={6}
						className="d-flex justify-content-end gap-4"
					>
						<button
							className={classNames(
								"btn shadow text-gray-700 hover-elevate-up",
								mode === "dark" ? "dark-theme-button-light" : "theme-button-light"
							)}
							onClick={handleFilterModalShow}
						>
							<FiFilter /> Filtro
						</button>

						<button
							className={classNames(
								"btn shadow text-gray-700 hover-elevate-up",
								mode === "dark" ? "dark-theme-button-light" : "theme-button-light",
								IS_HIDDEN_ON_DESKTOP && "d-none"
							)}
							onClick={handleAdmissionQueueModalShow}
						>
							<BsListUl /> Fila de admissão
						</button>
					</Col>
				</div>

				<InfiniteScroll
					height={getInfiniteScrollHeight()}
					className="row"
					dataLength={dataLength}
					next={() => handleNextPage(handleNextPageWrapper)}
					hasMore={hasResults && results?.length < paginatedBeds.meta?.total}
					loader={renderSkeletonLoader()}
				>
					{renderContent()}
				</InfiniteScroll>
			</div>

			<AdmissionQueue
				setPatientAdmissionModalShow={setPatientAdmissionModalShow}
				setAdmissionQueueIsOpen={setAdmissionQueueIsOpen}
				setQueueAdmissionData={setQueueAdmissionData}
				admissionQueueIsOpen={admissionQueueIsOpen}
				isPatientAdmitted={isPatientAdmitted}
			/>

			<PatientAdmissionModal
				setPatientAdmissionModalShow={setPatientAdmissionModalShow}
				patientAdmissionModalShow={patientAdmissionModalShow}
				surgicalTeamsOptions={surgicalTeamsOptions}
				setIsPatientAdmitted={setIsPatientAdmitted}
				queueAdmissionData={queueAdmissionData}
				isPatientAdmitted={isPatientAdmitted}
				onPatientAdmitted={handlePatientAdmitted}
			/>

			<FilterModal
				surgicalTeamsOptions={surgicalTeamsOptions}
				setFilterModalShow={setFilterModalShow}
				filterModalShow={filterModalShow}
				setFilters={setFilters}
			/>

			<AdmissionQueueModal
				setPatientAdmissionModalShow={setPatientAdmissionModalShow}
				setAdmissionQueueModalShow={setAdmissionQueueModalShow}
				admissionQueueModalShow={admissionQueueModalShow}
				setQueueAdmissionData={setQueueAdmissionData}
			/>
		</div>
	);
}
